import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Home as HomeIcon, Users, ClipboardList, CheckSquare, ArrowRight } from "lucide-react";
import { LEAD_STAGES } from "@/lib/crm";

export const Route = createFileRoute("/_authenticated/admin/")({
  head: () => ({ meta: [{ title: "Dashboard — Admin" }] }),
  component: AdminDashboard,
});

function AdminDashboard() {
  const { data: stats } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: async () => {
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const todayStr = new Date().toISOString().slice(0, 10);

      const [props, leadsMonth, tasksToday, openLeads] = await Promise.all([
        supabase.from("properties").select("id", { count: "exact", head: true }).neq("status", "borrador"),
        supabase.from("leads").select("id", { count: "exact", head: true }).gte("created_at", monthStart),
        supabase.from("tasks").select("id", { count: "exact", head: true }).eq("done", false).lte("due_date", todayStr),
        supabase.from("leads").select("id, stage, created_at, contact:contacts(full_name), property:properties(title, slug)").not("stage", "in", "(cerrado,perdido)").order("created_at", { ascending: false }).limit(5),
      ]);

      return {
        propertiesActive: props.count ?? 0,
        leadsThisMonth: leadsMonth.count ?? 0,
        tasksToday: tasksToday.count ?? 0,
        recentLeads: (openLeads.data ?? []) as Array<{
          id: string;
          stage: string;
          created_at: string;
          contact: { full_name: string } | null;
          property: { title: string; slug: string } | null;
        }>,
      };
    },
  });

  const cards = [
    { label: "Propiedades publicadas", value: stats?.propertiesActive ?? "—", icon: HomeIcon, to: "/admin/propiedades" as const },
    { label: "Leads del mes", value: stats?.leadsThisMonth ?? "—", icon: ClipboardList, to: "/admin/crm" as const },
    { label: "Tareas para hoy", value: stats?.tasksToday ?? "—", icon: CheckSquare, to: "/admin/tareas" as const },
    { label: "Contactos", value: "→", icon: Users, to: "/admin/contactos" as const },
  ];

  return (
    <div>
      <h1 className="text-2xl font-semibold text-foreground">Bienvenida, Dahiana</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Un resumen rápido de lo que está pasando en tu portal.
      </p>

      <div className="mt-8 grid gap-4 md:grid-cols-4">
        {cards.map((s) => (
          <Link
            key={s.label}
            to={s.to}
            className="group rounded-xl border border-border bg-card p-5 shadow-soft transition hover:shadow-card"
          >
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{s.label}</div>
              <s.icon className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
            </div>
            <div className="mt-2 text-3xl font-semibold text-foreground">{s.value}</div>
          </Link>
        ))}
      </div>

      <div className="mt-10 grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Leads recientes</h2>
            <Link to="/admin/crm" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
              Ver todos <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="mt-4 divide-y divide-border">
            {stats?.recentLeads.length === 0 && (
              <div className="py-6 text-center text-sm text-muted-foreground">Sin leads activos.</div>
            )}
            {stats?.recentLeads.map((l) => {
              const stage = LEAD_STAGES.find((s) => s.value === l.stage);
              return (
                <div key={l.id} className="flex items-center justify-between py-3">
                  <div>
                    <div className="text-sm font-medium">{l.contact?.full_name ?? "—"}</div>
                    <div className="text-xs text-muted-foreground line-clamp-1">
                      {l.property?.title ?? "Sin propiedad"}
                    </div>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${stage?.tone ?? "bg-secondary"}`}>
                    {stage?.label ?? l.stage}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-primary p-6 text-primary-foreground">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-accent">Acciones rápidas</h2>
          <div className="mt-4 space-y-2">
            <Link to="/admin/propiedades/nueva" className="flex items-center justify-between rounded-lg bg-white/10 px-4 py-3 text-sm hover:bg-white/20">
              Publicar nueva propiedad <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/admin/contactos" className="flex items-center justify-between rounded-lg bg-white/10 px-4 py-3 text-sm hover:bg-white/20">
              Agregar contacto <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/admin/tareas" className="flex items-center justify-between rounded-lg bg-white/10 px-4 py-3 text-sm hover:bg-white/20">
              Ver mis tareas <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
